import { FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDebounceValue } from 'usehooks-ts';
import { faBookmark as BookmarkRegular } from '@fortawesome/pro-regular-svg-icons';
import { faBookmark as BookmarkSolid } from '@fortawesome/pro-solid-svg-icons';
import {
  faCheckCircle,
  faExclamationCircle,
} from '@fortawesome/free-solid-svg-icons';
import { createBemBlockBuilder, ToolButton } from '@ebsco-ui/ebsco-ui';

import { Toast } from '@app/components';
import { useAppContext } from '@app/contexts';
import { sharedMessages } from '@app/translations';
import { defaultDebounceTimeMs, SetState } from '@app/constants';

import './SaveToolButton.scss';

export interface SaveToolButtonCallbacks {
  setItemSelected: SetState<boolean>;
  showNotification: (payload: {
    isSelected: boolean;
    isError?: boolean;
  }) => void;
}

interface SavedToolButtonProps {
  isSelected: boolean;
  onAuthorizedAction: (callbacks: SaveToolButtonCallbacks) => void;
  onUnauthorizedAction: (callbacks: SaveToolButtonCallbacks) => void;
  isDisabled?: boolean;
}

const cnBemToast = createBemBlockBuilder(['saveToolButtonToast']);

export const SaveToolButton: FC<SavedToolButtonProps> = ({
  isSelected,
  isDisabled = false,
  onAuthorizedAction,
  onUnauthorizedAction,
}) => {
  const { $t } = useIntl();
  const { isLoggedIn } = useAppContext();
  const [isItemSelected, setItemSelected] = useState(isSelected);
  const [isItemSelectedDebounced] = useDebounceValue(
    isItemSelected,
    defaultDebounceTimeMs
  );

  const showNotification = (payload: {
    isSelected: boolean;
    isError?: boolean;
  }): void => {
    if (!payload.isSelected && !isLoggedIn) {
      Toast({
        className: cnBemToast(),
        type: 'info',
        title: $t({
          id: 'saveToolButton.addedToYourSavedItems',
          defaultMessage: 'Added to your saved items.',
        }),
        subTitle: $t({
          id: 'saveToolButton.signInToYourAccount',
          defaultMessage:
            'Sign in to your account to keep your saved items forever.',
        }),
        icon: faCheckCircle,
      });
    }

    if (payload.isError) {
      Toast({
        className: cnBemToast(),
        type: 'error',
        title: $t({
          id: 'saveToolButton.weEncounteredAnError',
          defaultMessage:
            'We encountered an error while trying to save this record.',
        }),
        icon: faExclamationCircle,
      });
    }
  };

  const handleAction = (callbaks: SaveToolButtonCallbacks) => {
    const handler = isLoggedIn ? onAuthorizedAction : onUnauthorizedAction;

    return handler(callbaks);
  };

  useEffect(() => {
    if (isItemSelectedDebounced !== isSelected && !isDisabled) {
      handleAction({ setItemSelected, showNotification });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isItemSelectedDebounced, isSelected]);

  useEffect(() => {
    setItemSelected(isSelected);
  }, [isSelected]);

  return (
    <ToolButton
      title={
        isItemSelected ? $t(sharedMessages.unsave) : $t(sharedMessages.save)
      }
      isSelected={isItemSelected}
      defaultIcon={BookmarkRegular}
      selectedIcon={BookmarkSolid}
      disabled={isDisabled}
      onClick={() => setItemSelected(prevState => !prevState)}
    />
  );
};
